import { fromBase64Url, linkAccessToken, passwordAccessToken, unwrap, deriveSubkeys } from '../crypto/activityCrypto.js';
import { decryptEventValue, hexToBytes } from '../session/cryptoHelper.js';
import { SessionTimelineCache } from '../session/sessionCacheHelper.js';
import { alertSessionToBundle } from './alertSession.js';
import { buildSessionJournal } from '../session/sessionJournal.js';

/**
 * Relecture d'une session d'alerte à partir de son lien de suivi, pour le
 * dashboard : tout est déchiffré dans le navigateur de l'enquêteur.
 * - v2 : `…/session/{id}#k=<secret>` (le secret ne quitte jamais ce navigateur) ;
 */
export function parseSessionLink(link) {
    let url;
    try { url = new URL(String(link || '').trim()); } catch (_) { return null; }
    const match = /\/session\/([^/]+)/.exec(url.pathname);
    if (!match) return null;
    const sessionId = decodeURIComponent(match[1]);
    const secret = new URLSearchParams(url.hash.replace(/^#/, '')).get('k');
    if (secret) {
        try {
            const linkSecret = fromBase64Url(secret);
            if (linkSecret.length === 32) return { sessionId, access: { version: 2, linkSecret } };
        } catch (_) { /* lien abîmé */ }
        return null;
    }
    return null;
}

/**
 * Lien police : `…/relecture/{id}#s=<sel>&i=<itérations>`, mot de passe transmis à part.
 * Renvoie { sessionId, salt, iterations } ou null.
 */
export function parseReplayLink(link) {
    let url;
    try { url = new URL(String(link || '').trim()); } catch (_) { return null; }
    const match = /\/relecture\/([^/]+)/.exec(url.pathname);
    const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
    const iterations = Number(fragment.get('i'));
    let salt;
    try { salt = fromBase64Url(fragment.get('s') || ''); } catch (_) { return null; }
    if (!match || salt.length !== 16 || !Number.isInteger(iterations) || iterations < 100_000) return null;
    return { sessionId: decodeURIComponent(match[1]), salt, iterations };
}

/** En-têtes d'accès (v2 : jeton dérivé du secret ou du mot de passe) ; vide en v1. */
export async function sessionAccessHeaders(access) {
    const extra = access?.extraHeaders || {};
    if (access?.version !== 2) return { ...extra };
    if (access.password) return { ...extra, 'X-Activity-Access': await passwordAccessToken(access.password, access.salt, access.iterations) };
    return { ...extra, 'X-Activity-Access': await linkAccessToken(access.linkSecret) };
}

/** L'accès passe par l'en-tête dérivé du secret du lien : l'URL reste inchangée. */
export function withSessionAccess(url) {
    return url;
}

/**
 * Charge et déchiffre une session : détails, clés, état initial, événements.
 * Renvoie le bundle du lecteur, les clés de lecture et les originaux chiffrés
 * (pour le dossier de preuve).
 */
export async function loadAlertSession({ apiUrl, sessionId, access, fetchImpl = fetch }) {
    const base = `${apiUrl.replace(/\/+$/, '')}/api/session/${encodeURIComponent(sessionId)}`;
    const headers = await sessionAccessHeaders(access);
    const getJson = async (resource, { optional = false } = {}) => {
        const response = await fetchImpl(withSessionAccess(`${base}/${resource}`, access), { headers, cache: 'no-store' });
        if (!response.ok) {
            if (optional) return null;
            throw new Error(response.status === 403 || response.status === 401
                ? 'Lien refusé : il est incomplet, révoqué ou la session a expiré.'
                : `Session indisponible (${response.status}).`);
        }
        return response.json();
    };

    const details = await getJson('details');
    if (!details?.session) throw new Error('Session introuvable.');

    let eventsKey;
    let mediaKey;
    let activityKey = null;
    if (access.version === 2) {
        const { envelope } = await getJson('wraps/access');
        activityKey = await unwrap(envelope, sessionId, access.password ? { password: access.password } : { linkSecret: access.linkSecret });
        const keys = await deriveSubkeys(activityKey);
        eventsKey = keys.events;
        mediaKey = keys.mediaHls;
    } else {
        if (!details.session.pswdSession) throw new Error('Clé de la session indisponible.');
        eventsKey = hexToBytes(details.session.pswdSession);
        mediaKey = eventsKey;
    }

    const initial = await getJson('initial-state', { optional: true });
    const initialState = initial?.initialState
        ? JSON.parse((await decryptEventValue(initial.initialState, eventsKey)) || 'null')
        : null;
    const history = await getJson('historyEvent');
    const events = [];
    for (const stored of history?.events || []) {
        const raw = typeof stored.eventData === 'string' ? stored.eventData.trim() : '';
        if (!raw || raw.startsWith('{')) continue; // jamais d'événement en clair
        const clear = await decryptEventValue(raw, eventsKey);
        if (!clear) continue;
        try {
            const decoded = JSON.parse(clear);
            events.push({
                ...stored, eventData: clear, eventType: decoded.eventType || decoded.property || stored.eventType,
                timestamp: Number(decoded.timestamp) || Number(stored.timestamp),
            });
        } catch (_) { /* événement illisible ignoré */ }
    }
    const cache = new SessionTimelineCache(initialState);
    cache.replaceEvents(events);
    const { positions } = cache.getLatestState();
    const playlistUrl = withSessionAccess(`${base}/media/playlist.m3u8`, access);
    const journal = buildSessionJournal(events, cache.initialSnapshot);
    const bundle = alertSessionToBundle({ sessionId, details: details.session, positions, events, playlistUrl, journal });
    return {
        // Pour le dossier : événements en clair, journal, fiche de la session.
        events, journal, details: details.session, initialState,
        // activityKey (v2) : permet à l'administrateur de créer un lien police.
        bundle, headers, eventsKey, mediaKey, activityKey,
        originals: { stored: history?.events || [], initialState: initial?.initialState || null },
    };
}
